// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { SendableMessageType } from "@sendbird/uikit-react/types/utils";
import {
  ImageIcon,
  MicIcon,
  VideoIcon,
  XIcon,
  FileImageIcon,
} from "lucide-react";
import { HeicImage } from "@/components/ui/heic-image";
import { getDisplayText, MENTOR_LANGUAGE } from "@/lib/sendbird/translation";

interface QuotedMessageProps {
  message: SendableMessageType;
  onQuoteClick?: () => void;
}

export function QuotedMessage({ message, onQuoteClick }: QuotedMessageProps) {
  return (
    <div
      className="-mb-4 flex cursor-pointer flex-row items-center justify-between gap-2 rounded-lg bg-gray-100 p-2 pb-4 active:bg-gray-200"
      onClick={onQuoteClick}
    >
      <div className="flex flex-grow flex-row items-center">
        <QuotedMessageContent message={message} />
      </div>
      <div className="flex items-center gap-2">
        <QuotedMessagePreview message={message} />
      </div>
    </div>
  );
}

interface NewQuotedMessageProps {
  message: SendableMessageType;
  handleRemoveQuote?: () => void;
}

export function NewQuotedMessage({
  message,
  handleRemoveQuote,
}: NewQuotedMessageProps) {
  return (
    <div className="flex flex-row items-center justify-between rounded-lg bg-gray-100 p-2">
      <div className="flex flex-grow flex-row items-center gap-2">
        <div className="h-10 w-1 rounded-full bg-blue-500"></div>
        <QuotedMessageContent message={message} />
      </div>
      <div className="flex items-center gap-2">
        <QuotedMessagePreview message={message} />
        {handleRemoveQuote && (
          <button onClick={handleRemoveQuote}>
            <XIcon className="h-4 w-4 text-gray-500" />
          </button>
        )}
      </div>
    </div>
  );
}

function QuotedMessageContent({ message }: { message: SendableMessageType }) {
  const isImage = message?.type?.includes("image");
  const isAudio = message?.type?.includes("audio");
  const isVideo = message?.type?.includes("video");
  const isGif = (message as any)?.customType === "GIF_MESSAGE";
  const { text: textContent } = getDisplayText(
    message as { message: string; translations: object },
    MENTOR_LANGUAGE,
  );

  return (
    <div className="flex max-w-md flex-col">
      <span className="text-sm font-semibold text-blue-500">
        {message.sender?.nickname}
      </span>
      <div className="flex items-center gap-2">
        {isImage && <ImageIcon className="h-4 w-4 text-gray-500" />}
        {isAudio && <MicIcon className="h-4 w-4 text-gray-500" />}
        {isVideo && <VideoIcon className="h-4 w-4 text-gray-500" />}
        {isGif && <FileImageIcon className="h-4 w-4 text-gray-500" />}
        <span className="max-w-2xl truncate text-sm text-gray-700">
          {isImage && "Photo"}
          {isAudio && "Audio"}
          {isVideo && "Video"}
          {isGif && "GIF"}
          {!isImage && !isAudio && !isVideo && !isGif && textContent}
        </span>
      </div>
    </div>
  );
}

function QuotedMessagePreview({ message }: { message: SendableMessageType }) {
  const isImage = message?.type?.includes("image");
  const isAudio = message?.type?.includes("audio");
  const isVideo = message?.type?.includes("video");
  const isGif = (message as any)?.customType === "GIF_MESSAGE";

  if (isGif) {
    return (
      <div className="h-10 w-10 overflow-hidden rounded">
        <img
          src={(message as any).message}
          alt="GIF Preview"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  if (isImage) {
    return (
      <div className="h-10 w-10 overflow-hidden rounded">
        <HeicImage
          src={message.url}
          mimeType={message.type}
          alt="Preview"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  if (isAudio) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
        <MicIcon className="h-6 w-6 text-gray-500" />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
        <VideoIcon className="h-6 w-6 text-gray-500" />
      </div>
    );
  }

  return null;
}
